import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { CreateWorkspaceModal } from "./Modals/CreateWorkspaceModal";
import { listWorkspaces, moveWorkspaceToTrash, Workspace, Track, fetchWorkspaces } from "../data/schema";
import { WorkspacesView } from "./WorkspacesView";
import { WorkspacesList } from "./WorkspacesList";
import {
  WORKSPACE_FOCUS_REQUEST_EVENT,
  WORKSPACE_TRACK_MENU_REQUEST_EVENT,
} from "components/AICompose/events";
import { DeviceSize } from "constants/deviceSize";

type RightPanelProps = {
  workspaceSearch: string;
  onChangeSearch: (value: string) => void;
  className?: string;
  onPlayTrack: (track: Track | null, sourceTracks?: Track[]) => void;
  hasPlayer?: boolean;
  playingTrack?: Track | null;
  isPlaying?: boolean;
  onTogglePlay?: () => void;
  onPersonaCreated?: () => void;
  onOpenComposeModal?: () => void;
  isMobile?: boolean;
};

export const RightPanel: React.FC<RightPanelProps> = ({
  className,
  workspaceSearch,
  onChangeSearch,
  onPlayTrack,
  hasPlayer = false,
  playingTrack,
  isPlaying,
  onTogglePlay,
  onPersonaCreated,
  onOpenComposeModal,
  isMobile,
}) => {
  const [openCreate, setOpenCreate] = useState(false);
  const [workspaces, setWorkspaces] = useState<Workspace[]>(listWorkspaces());
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState(false);
  const [focusRequestToken, setFocusRequestToken] = useState<number>(0);
  const [trackMenuRequestToken, setTrackMenuRequestToken] = useState<number>(0);

  const refresh = useCallback(async () => {
    setWorkspaces(listWorkspaces());
    window.dispatchEvent(new CustomEvent('workspaceListUpdated'));
    setIsLoadingWorkspaces(true);
    try {
      const fresh = await fetchWorkspaces();
      setWorkspaces(fresh);
      window.dispatchEvent(new CustomEvent('workspaceListUpdated'));
    } catch (error) {
      console.error("Unable to fetch workspaces", error);
      setWorkspaces(listWorkspaces());
    } finally {
      setIsLoadingWorkspaces(false);
    }
  }, []);

  const handleMoveToTrash = useCallback(
    async (workspaceId: string) => {
      setIsLoadingWorkspaces(true);
      try {
        await moveWorkspaceToTrash(workspaceId);
      } catch (error) {
        console.error("Unable to move workspace to trash", error);
      } finally {
        await refresh();
      }
    },
    [refresh]
  );

  useEffect(() => {
    const handleFocusRequest = (event: Event) => {
      const detail = (event as CustomEvent<{ workspaceId?: string | null; openMenu?: boolean }>).detail || {};
      setFocusRequestToken(Date.now());
      const lastId = (() => {
        try {
          return (
            detail.workspaceId ||
            localStorage.getItem('aiCompose.saveWorkspaceId') ||
            localStorage.getItem('aiCompose.lastWorkspaceId')
          );
        } catch {
          return null;
        }
      })();
      const targetId = detail.workspaceId || lastId || workspaces[0]?.id || null;
      if (targetId) {
        if (targetId !== selectedId) {
          setSelectedId(targetId);
        }
        if (detail.openMenu) {
          setTimeout(() => {
            setTrackMenuRequestToken(Date.now());
          }, 0);
        }
      } else if (detail.openMenu) {
        setTimeout(() => {
          setTrackMenuRequestToken(Date.now());
        }, 0);
      }
    };
    window.addEventListener(WORKSPACE_FOCUS_REQUEST_EVENT, handleFocusRequest as EventListener);
    return () => {
      window.removeEventListener(WORKSPACE_FOCUS_REQUEST_EVENT, handleFocusRequest as EventListener);
    };
  }, [selectedId, workspaces]);

  useEffect(() => {
    const handleTrackMenuRequest = () => {
      setTrackMenuRequestToken(Date.now());
    };
    window.addEventListener(WORKSPACE_TRACK_MENU_REQUEST_EVENT, handleTrackMenuRequest as EventListener);
    return () => {
      window.removeEventListener(WORKSPACE_TRACK_MENU_REQUEST_EVENT, handleTrackMenuRequest as EventListener);
    };
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const handleTrackCreated = () => {
      refresh();
    };
    window.addEventListener('trackCreated', handleTrackCreated as EventListener);
    return () => window.removeEventListener('trackCreated', handleTrackCreated as EventListener);
  }, [refresh]);

  useEffect(() => {
    if (!selectedId) {
      refresh();
    }
  }, [selectedId, refresh]);

  const selectedWorkspace = selectedId ? workspaces.find(w => w.id === selectedId) : null;

  return (
    <Container className={className}>
      <PanelsWrapper>
        <SlidingPanel $visible={!selectedWorkspace} $side="left">
          <WorkspacesList
            workspaces={workspaces}
            workspaceSearch={workspaceSearch}
            onChangeSearch={onChangeSearch}
            onOpenCreate={() => setOpenCreate(true)}
            onSelect={(id) => setSelectedId(id)}
            onMoveToTrash={handleMoveToTrash}
            isLoading={isLoadingWorkspaces}
            onOpenComposeModal={onOpenComposeModal}
            hasPlayer={hasPlayer}
          />
        </SlidingPanel>
        <SlidingPanel $visible={!!selectedWorkspace} $side="right">
          {selectedWorkspace && (
            <WorkspacesView
              workspaces={workspaces}
              workspaceSearch={workspaceSearch}
              onChangeSearch={onChangeSearch}
              onSelect={(id) => setSelectedId(id)}
              onMoveToTrash={handleMoveToTrash}
              onBack={() => setSelectedId(null)}
              selectedWorkspace={selectedWorkspace}
              onPlayTrack={onPlayTrack}
              hasPlayer={hasPlayer}
              playingTrack={playingTrack}
              isPlaying={isPlaying}
              onTogglePlay={onTogglePlay}
              onWorkspaceRenamed={() => refresh()}
              focusRequestToken={focusRequestToken}
              trackMenuRequestToken={trackMenuRequestToken}
              onPersonaCreated={onPersonaCreated}
            />
          )}
        </SlidingPanel>
      </PanelsWrapper>
      <CreateWorkspaceModal open={openCreate} onClose={() => setOpenCreate(false)} onCreated={() => refresh()} />
    </Container>
  );
};

const Container = styled.div`
  flex: 1;
  width: 100%;
  min-width: 0; /* не расширять колонку */
  background-color: white;
  padding: 20px 0px 0px 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow-x: hidden;
  overflow-y: hidden; /* скролл отдаем внутренним вьюхам (WorkspacesList / WorkspacesView) */
  height: 100%;
  box-sizing: border-box;

   @media (max-width: ${DeviceSize.TabletMedium}) {
    padding: 12px 16px 0px 16px;
  }
`;

const PanelsWrapper = styled.div`
  position: relative;
  flex: 1;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto;
  overflow: hidden;
`;

const SlidingPanel = styled.div<{ $visible: boolean; $side: "left" | "right" }>`
  grid-column: 1 / -1;
  grid-row: 1 / -1;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: transform 0.4s cubic-bezier(0.22, 0.61, 0.36, 1), opacity 0.4s ease;
  will-change: transform, opacity;
  transform: ${({ $visible, $side }) => {
    if ($visible) return "translateX(0%)";
    return $side === "left" ? "translateX(-100%)" : "translateX(100%)";
  }};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};
`;

// list-specific styled components moved to WorkspacesList.tsx

export default RightPanel;


